import { MIME_TYPES } from '../constants/mime-type-constants';


/**
 * Checks if a given string is a valid URL.
 *
 * @param {string} url The string to check.
 * @returns {boolean} True if the string is a valid URL, false otherwise.
 */
export const isUrl = (url) => {
    if (typeof url !== 'string') {
        return false;
    }

    try {
        new URL(url);
        return true;
    } catch (err) {
        return false;
    }
}

/**
 * Detects the appropriate Video.js source type (MIME type) for a given URL.
 *
 * @param {string} url The URL of the video source.
 * @returns {string | null} The detected MIME type, or null if no suitable type could be determined.
 */
export const detectVideoTypeFromUrl = (url) => {
    if (!isUrl(url)) {
        return null;
    }

    const urlObj = new URL(url);
    const pathname = urlObj.pathname;
    const searchParams = urlObj.searchParams;

    // 1. Check for 'type', 'mimeType', or 'mime-type' query parameters.
    const typeParam = searchParams.get('type') || searchParams.get('mimeType') || searchParams.get('mime-type');
    if (typeParam) {
        // Normalize the type parameter to lowercase for case-insensitive comparison.
        const lowerTypeParam = typeParam.toLowerCase();

        // Iterate through MIME_TYPES and check for matches (both key and value).
        for (const key in MIME_TYPES) {
            if (MIME_TYPES.hasOwnProperty(key)) {
                const mimeType = MIME_TYPES[key];
                if (mimeType.toLowerCase() === lowerTypeParam || key.toLowerCase() === lowerTypeParam) {
                    return mimeType; // Return the *standardized* MIME type from MIME_TYPES.
                }
            }
        }
        // If no direct match in MIME_TYPES, return the provided (but potentially non-standard) typeParam.
        return typeParam;
    }

    // 2. Check file extension (common case).  Make this more robust.
    const extension = pathname.split('.').pop().toLowerCase();

    // Use a function for extension mapping, making it easy to add/modify types
    const mimeTypeFromExtension = getMimeTypeFromExtension(extension);
    if (mimeTypeFromExtension) {
        return mimeTypeFromExtension;
    }


    // 3. Check for specific patterns in pathname and searchParams (for DASH and HLS).
    if (pathname.includes('manifest.mpd') || searchParams.toString().includes('mpd') || pathname.endsWith('.mpd')) {
        return MIME_TYPES.DASH;
    }
    // More robust HLS checking:
    if (pathname.includes('.m3u8') || pathname.includes('/playlist/') || searchParams.toString().includes('m3u8') || pathname.endsWith('.m3u8')) {
        return MIME_TYPES.HLS;
    }

    // 4. No match found
    return null;
};

/**
 * Helper function to map file extensions to MIME types.
 *
 * @param {string} extension The file extension (without the leading dot).
 * @returns {string | null} The corresponding MIME type, or null if not found.
 */
const getMimeTypeFromExtension = (extension) => {
    switch (extension) {
        case 'mp4':
            return MIME_TYPES.MP4;
        case 'm3u8':
            return MIME_TYPES.HLS;
        case 'mpd':
            return MIME_TYPES.DASH;
        default:
            return null;
    }
};
